// client/src/pages/PublicBookingThankYouPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { fetchUserBySlug } from '../api/bookingForm';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaEnvelope, FaPhone, FaBuilding, FaArrowRight } from 'react-icons/fa';

const PublicBookingThankYouPage = () => {
    const { userSlug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const clientData = location.state || {};

    useEffect(() => {
        const loadUserData = async () => {
            try {
                setLoading(true);
                const response = await fetchUserBySlug(userSlug);
                if (response.success) {
                    setUser(response.user);
                } else {
                    setError('Could not load business information.');
                }
            } catch (err) {
                console.error('Error fetching user:', err);
                setError('An error occurred loading this page.');
            } finally {
                setLoading(false);
            }
        };

        loadUserData();
    }, [userSlug]);

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut",
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-screen bg-red-50">
                <div className="bg-white p-8 rounded-xl shadow-lg border border-red-100 max-w-md text-center">
                    <div className="text-red-500 text-5xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen font-sans py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
            {/* Background decoration */}
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-blue-100 blur-3xl opacity-50"></div>
                <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-indigo-100 blur-3xl opacity-50"></div>
            </div>

            <motion.div
                className="relative z-10 w-full max-w-2xl bg-white/90 backdrop-blur-sm shadow-2xl rounded-2xl overflow-hidden border border-white/50"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Gradient Top Bar */}
                <div className="h-2 bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500"></div>

                <div className="p-8 md:p-12 text-center">
                    {/* Success Icon */}
                    <motion.div
                        className="flex justify-center mb-6"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.2 }}
                    >
                        <div className="bg-green-100 p-4 rounded-full">
                            <FaCheckCircle className="text-6xl text-green-500" />
                        </div>
                    </motion.div>

                    <motion.h1
                        className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4"
                        variants={itemVariants}
                    >
                        Request Received!
                    </motion.h1>

                    <motion.p
                        className="text-lg text-gray-600 mb-8 max-w-lg mx-auto"
                        variants={itemVariants}
                    >
                        Thanks for booking with <span className="font-semibold text-gray-900">{user?.companyName || 'us'}</span>. We've received your details and will be in touch shortly to confirm your appointment.
                    </motion.p>

                    {/* Next Steps */}
                    <motion.div
                        className="bg-gray-50/80 rounded-xl p-6 mb-8 text-left border border-gray-100"
                        variants={itemVariants}
                    >
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">What Happens Next?</h3>
                        <div className="space-y-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs mt-0.5">1</div>
                                <p className="ml-3 text-gray-600 text-sm">We'll review your request and check our availability.</p>
                            </div>
                            <div className="flex items-start">
                                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs mt-0.5">2</div>
                                <p className="ml-3 text-gray-600 text-sm">We'll reach out to confirm the final details.</p>
                            </div>
                            <div className="flex items-start">
                                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs mt-0.5">3</div>
                                <p className="ml-3 text-gray-600 text-sm">We'll reach out if we need any more information.</p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Contact Info Grid */}
                    {user && (
                        <motion.div
                            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"
                            variants={itemVariants}
                        >
                            <div className="p-4 rounded-lg border border-gray-100 bg-white/50 hover:bg-white transition-colors">
                                <div className="flex items-center justify-center text-gray-400 mb-2">
                                    <FaEnvelope className="mr-2" /> Email Us
                                </div>
                                <a href={`mailto:${user.email}`} className="text-blue-600 font-medium hover:underline block truncate">
                                    {user.email}
                                </a>
                            </div>
                            {user.companyPhone && (
                                <div className="p-4 rounded-lg border border-gray-100 bg-white/50 hover:bg-white transition-colors">
                                    <div className="flex items-center justify-center text-gray-400 mb-2">
                                        <FaPhone className="mr-2" /> Call Us
                                    </div>
                                    <a href={`tel:${user.companyPhone}`} className="text-blue-600 font-medium hover:underline block">
                                        {user.companyPhone}
                                    </a>
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Action Buttons */}
                    <motion.div variants={itemVariants}>
                        {/* If we had a "Book Another" flow, we could add it here. For now, maybe just a generic "Done" or link to website if available */}
                        {/* Since this is a public page, they might not have a "Home" to go to in the app context, but we can reload or just show a "Close" hint */}
                        <p className="text-sm text-gray-400">You can close this window now.</p>
                    </motion.div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 flex justify-between items-center">
                    <div className="flex items-center">
                        {user?.companyLogoUrl ? (
                            <img src={user.companyLogoUrl} alt="Logo" className="h-6 w-auto opacity-60 grayscale hover:grayscale-0 transition-all" />
                        ) : (
                            <span className="text-sm font-bold text-gray-400">{user?.companyName}</span>
                        )}
                    </div>
                    <div className="text-xs text-gray-400">
                        Powered by <a href="/" className="font-semibold text-blue-500 hover:text-blue-600 transition-colors">SwiftBidPro</a>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default PublicBookingThankYouPage;
